/**
 * Chat Service
 * Real-time Firestore-backed chat with rooms and messages
 */

import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  Timestamp,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
  deleteField,
  writeBatch,
} from 'firebase/firestore';

// ============ TYPES ============

export interface ChatRoom {
  id: string;
  participants: string[];  // user IDs
  participantNames: Record<string, string>;
  participantAvatars: Record<string, string>;
  lastMessage?: string;
  lastMessageAt?: any;
  lastSenderId?: string;
  lastReadAt?: Record<string, any>;  // per-user last-read timestamp
  createdAt?: any;
  type: 'direct' | 'group' | 'artist';
  // Group-only fields
  groupName?: string;
  groupAvatar?: string;
  createdBy?: string;
  adminIds?: string[];
  /** Private (default) = invite-only, hidden from discovery. Public = anyone can find + request to join. */
  visibility?: 'private' | 'public';
  // Artist-room fields — one shared room per artist for all connected creators
  artistId?: string;
  artistName?: string;
  artistImageUrl?: string;
}

export interface SharedContent {
  contentType: 'track' | 'release' | 'artist' | 'playlist' | 'event' | 'video' | 'marketplace' | 'profile' | 'link' | 'page' | 'collab';
  contentId?: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  link: string;        // internal app link e.g. /artists/slug or /music/id
  audioUrl?: string;   // if shareable as playable audio
}

export interface MediaMessageData {
  type: 'audio' | 'video' | 'image';
  url: string;
  duration?: number;
  mimeType?: string;
}

export interface CallTraceData {
  callType: 'audio' | 'video';
  /** ended = answered then hung up; missed = no answer; declined = receiver rejected; cancelled = caller cancelled while ringing */
  callStatus: 'ended' | 'missed' | 'declined' | 'cancelled';
  durationSeconds?: number;   // only for 'ended'
  callerId: string;
}

/** A snapshot of the sender's own availability slots at share-time — not a
 *  live/synced booking. See src/services/availability.ts for the source data. */
export interface SharedAvailabilitySlot {
  type: 'rehearsal' | 'gig' | 'meeting' | 'recording' | 'event' | 'other';
  startAt: string;
  endAt: string;
  note?: string;
}

export interface ChatMessage {
  id: string;
  roomId: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  text: string;
  createdAt: any;
  type: 'text' | 'system' | 'shared_content' | 'audio' | 'video' | 'image' | 'call' | 'availability_slots';
  sharedContent?: SharedContent;
  mediaData?: MediaMessageData;
  callTrace?: CallTraceData;
  availabilitySlots?: SharedAvailabilitySlot[];
  edited?: boolean;
  editedAt?: any;
}

// ============ ROOMS ============

/**
 * Get or create a direct message room between two users
 */
export async function getOrCreateDMRoom(
  userId: string,
  userName: string,
  userAvatar: string,
  otherUserId: string,
  otherUserName: string,
  otherUserAvatar: string
): Promise<ChatRoom> {
  // Look for existing DM room
  const q = query(
    collection(db, 'chatRooms'),
    where('type', '==', 'direct'),
    where('participants', 'array-contains', userId)
  );
  const snapshot = await getDocs(q);

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    if (data.participants.includes(otherUserId)) {
      // Refresh participant names and avatars if they've changed
      const existingNames = data.participantNames || {};
      const existingAvatars = data.participantAvatars || {};
      const needsUpdate =
        existingNames[userId] !== userName ||
        existingNames[otherUserId] !== otherUserName ||
        (userAvatar && existingAvatars[userId] !== userAvatar) ||
        (otherUserAvatar && existingAvatars[otherUserId] !== otherUserAvatar);
      if (needsUpdate) {
        const updates: Record<string, string> = {};
        updates[`participantNames.${userId}`] = userName;
        updates[`participantNames.${otherUserId}`] = otherUserName;
        if (userAvatar) updates[`participantAvatars.${userId}`] = userAvatar;
        if (otherUserAvatar) updates[`participantAvatars.${otherUserId}`] = otherUserAvatar;
        await updateDoc(docSnap.ref, updates);
        return {
          id: docSnap.id,
          ...data,
          participantNames: { ...existingNames, [userId]: userName, [otherUserId]: otherUserName },
          participantAvatars: {
            ...existingAvatars,
            ...(userAvatar ? { [userId]: userAvatar } : {}),
            ...(otherUserAvatar ? { [otherUserId]: otherUserAvatar } : {}),
          },
        } as ChatRoom;
      }
      return { id: docSnap.id, ...data } as ChatRoom;
    }
  }

  // Create new room
  const roomData = {
    type: 'direct',
    participants: [userId, otherUserId],
    participantNames: { [userId]: userName, [otherUserId]: otherUserName },
    participantAvatars: { [userId]: userAvatar, [otherUserId]: otherUserAvatar },
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatRooms'), roomData);
  return { id: docRef.id, ...roomData } as ChatRoom;
}

/**
 * Create a new group chat room
 */
export async function createGroupRoom(
  creatorId: string,
  memberIds: string[],        // all members including creator
  memberNames: Record<string, string>,
  memberAvatars: Record<string, string>,
  groupName: string
): Promise<ChatRoom> {
  const roomData = {
    type: 'group',
    groupName,
    createdBy: creatorId,
    adminIds: [creatorId],
    visibility: 'private' as const,  // private by default — owner can flip in group settings
    participants: [...new Set([creatorId, ...memberIds])],
    participantNames: memberNames,
    participantAvatars: memberAvatars,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatRooms'), roomData);
  return { id: docRef.id, ...roomData } as ChatRoom;
}

/**
 * Flip a group's visibility. Only an admin (or the creator) should call this
 * — the UI gates that; rules should too.
 */
export async function setGroupVisibility(roomId: string, visibility: 'private' | 'public'): Promise<void> {
  const { doc, updateDoc } = await import('firebase/firestore');
  await updateDoc(doc(db, 'chatRooms', roomId), { visibility });
}

/**
 * Find all PUBLIC groups (used by the public-groups discovery view).
 * Excludes groups the user is already in to keep the list useful.
 */
export async function getPublicGroups(excludeUserId?: string, max = 50): Promise<ChatRoom[]> {
  const q = query(
    collection(db, 'chatRooms'),
    where('type', '==', 'group'),
    where('visibility', '==', 'public')
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as ChatRoom))
    .filter(r => !excludeUserId || !r.participants.includes(excludeUserId))
    .sort((a, b) => (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0))
    .slice(0, max);
}

/**
 * Get all chat rooms for a user
 */
export async function getUserChatRooms(userId: string): Promise<ChatRoom[]> {
  const q = query(
    collection(db, 'chatRooms'),
    where('participants', 'array-contains', userId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() } as ChatRoom))
    .sort((a, b) => {
      const aTime = a.lastMessageAt?.toMillis?.() || 0;
      const bTime = b.lastMessageAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
}

/**
 * Subscribe to user's chat rooms (real-time)
 */
export function subscribeChatRooms(
  userId: string,
  callback: (rooms: ChatRoom[]) => void
): () => void {
  const q = query(
    collection(db, 'chatRooms'),
    where('participants', 'array-contains', userId)
  );
  return onSnapshot(q, (snapshot) => {
    const rooms = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() } as ChatRoom))
      .sort((a, b) => {
        const aTime = a.lastMessageAt?.toMillis?.() || 0;
        const bTime = b.lastMessageAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    callback(rooms);
  });
}

/**
 * Add new members to an existing group chat room
 */
export async function addMembersToGroup(
  roomId: string,
  newMembers: Array<{ id: string; name: string; avatar: string }>
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updates: Record<string, any> = {
    participants: arrayUnion(...newMembers.map(m => m.id)),
  };
  for (const m of newMembers) {
    updates[`participantNames.${m.id}`] = m.name;
    updates[`participantAvatars.${m.id}`] = m.avatar || '';
  }
  await updateDoc(doc(db, 'chatRooms', roomId), updates);
}

/**
 * Remove a member from a group chat room
 */
export async function removeMemberFromGroup(roomId: string, memberId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updates: Record<string, any> = {
    participants: arrayRemove(memberId),
  };
  updates[`participantNames.${memberId}`] = deleteField();
  updates[`participantAvatars.${memberId}`] = deleteField();
  await updateDoc(doc(db, 'chatRooms', roomId), updates);
}

/**
 * Assign or revoke admin role in a group chat
 */
export async function setGroupAdmin(roomId: string, memberId: string, isAdmin: boolean): Promise<void> {
  await updateDoc(doc(db, 'chatRooms', roomId), {
    adminIds: isAdmin ? arrayUnion(memberId) : arrayRemove(memberId),
  });
}

/**
 * Delete a group chat room and all its messages
 */
export async function deleteGroupRoom(roomId: string): Promise<void> {
  // Batch-delete all messages in this room
  const msgsSnap = await getDocs(
    query(collection(db, 'chatMessages'), where('roomId', '==', roomId))
  );
  const batch = writeBatch(db);
  msgsSnap.docs.forEach(d => batch.delete(d.ref));
  batch.delete(doc(db, 'chatRooms', roomId));
  await batch.commit();
}

// ============ ARTIST ROOMS ============

/**
 * Get or create the shared crew room for an artist.
 * One room per artist; all connected creator users are participants.
 * Safe to call multiple times — idempotent.
 */
export async function getOrCreateArtistRoom(
  artistId: string,
  artistName: string,
  artistImageUrl: string,
  connectedUsers: Array<{ id: string; displayName?: string; profileImageUrl?: string }>
): Promise<ChatRoom> {
  // Check for existing room
  const existing = await getDocs(
    query(collection(db, 'chatRooms'), where('artistId', '==', artistId))
  );
  if (!existing.empty) {
    const room = { id: existing.docs[0].id, ...existing.docs[0].data() } as ChatRoom;
    // Sync participants if new members were added
    const currentIds = new Set(room.participants);
    const newIds = connectedUsers.map(u => u.id).filter(id => !currentIds.has(id));
    if (newIds.length > 0) {
      const updatedParticipants = [...room.participants, ...newIds];
      const updatedNames = { ...(room.participantNames || {}) };
      const updatedAvatars = { ...(room.participantAvatars || {}) };
      for (const u of connectedUsers) {
        if (!currentIds.has(u.id)) {
          updatedNames[u.id] = u.displayName || 'Member';
          updatedAvatars[u.id] = u.profileImageUrl || '';
        }
      }
      await updateDoc(doc(db, 'chatRooms', room.id), {
        participants: updatedParticipants,
        participantNames: updatedNames,
        participantAvatars: updatedAvatars,
      });
      return { ...room, participants: updatedParticipants, participantNames: updatedNames, participantAvatars: updatedAvatars };
    }
    return room;
  }

  // Create fresh room
  const participantNames: Record<string, string> = {};
  const participantAvatars: Record<string, string> = {};
  for (const u of connectedUsers) {
    participantNames[u.id] = u.displayName || 'Member';
    participantAvatars[u.id] = u.profileImageUrl || '';
  }
  const roomData = {
    type: 'artist' as const,
    artistId,
    artistName,
    artistImageUrl: artistImageUrl || '',
    participants: connectedUsers.map(u => u.id),
    participantNames,
    participantAvatars,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatRooms'), roomData);
  return { id: docRef.id, ...roomData } as ChatRoom;
}

/**
 * Post a system notification in an artist's crew room when someone new follows them.
 * No-ops silently if the artist has no crew room yet.
 */
export async function notifyArtistNewFollower(
  artistId: string,
  artistName: string,
  followerName: string,
  followerImageUrl?: string
): Promise<void> {
  try {
    const snap = await getDocs(
      query(collection(db, 'chatRooms'), where('artistId', '==', artistId))
    );
    if (snap.empty) return;
    const roomId = snap.docs[0].id;
    const messageData = {
      roomId,
      senderId: 'system',
      senderName: 'Activity',
      text: `📢 ${followerName} started following ${artistName}`,
      type: 'system',
      createdAt: serverTimestamp(),
    };
    await addDoc(collection(db, 'chatRooms', roomId, 'messages'), messageData);
    await updateDoc(doc(db, 'chatRooms', roomId), {
      lastMessage: messageData.text,
      lastMessageAt: serverTimestamp(),
      lastSenderId: 'system',
    });
  } catch (err) {
    console.error('notifyArtistNewFollower error:', err);
  }
}

// ============ MESSAGES ============

/**
 * Send a message in a room
 */
export async function sendMessage(
  roomId: string,
  senderId: string,
  senderName: string,
  text: string,
  senderAvatar?: string
): Promise<string> {
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text,
    type: 'text',
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);

  // Update room's last message — include senderName and text preview for toast notifications
  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: text,
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: text,
  });

  return docRef.id;
}

/**
 * Send an audio or video message in a room
 */
export async function sendImageMessage(
  roomId: string,
  senderId: string,
  senderName: string,
  imageUrl: string,
  senderAvatar?: string
): Promise<string> {
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text: '📷 Image',
    type: 'image' as const,
    mediaData: { type: 'image', url: imageUrl },
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);
  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: '📷 Image',
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: '📷 Image',
  });
  return docRef.id;
}

export async function sendMediaMessage(
  roomId: string,
  senderId: string,
  senderName: string,
  mediaType: 'audio' | 'video',
  mediaUrl: string,
  duration: number,
  mimeType: string,
  senderAvatar?: string
): Promise<string> {
  const typeLabel = mediaType === 'audio' ? '🎙️ Audio message' : '📹 Video message';
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text: `${typeLabel} (${Math.floor(duration)}s)`,
    type: mediaType,
    mediaData: {
      type: mediaType,
      url: mediaUrl,
      duration,
      mimeType,
    },
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);

  // Update room's last message
  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: typeLabel,
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: typeLabel,
  });

  return docRef.id;
}

/**
 * Share a piece of content (track, release, artist, etc.) in a chat room
 */
export async function sendSharedContentMessage(
  roomId: string,
  senderId: string,
  senderName: string,
  content: SharedContent,
  senderAvatar?: string
): Promise<string> {
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text: `Shared: ${content.title}`,
    type: 'shared_content' as const,
    sharedContent: content,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);

  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: `📎 ${content.title}`,
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: `📎 ${content.title}`,
  });

  return docRef.id;
}

/**
 * Share one or more of the sender's own availability slots (rehearsal/gig/
 * meeting/recording/event/other open windows) as a single card in a chat room.
 */
export async function sendAvailabilitySlotsMessage(
  roomId: string,
  senderId: string,
  senderName: string,
  slots: SharedAvailabilitySlot[],
  senderAvatar?: string
): Promise<string> {
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text: `Shared availability (${slots.length} slot${slots.length === 1 ? '' : 's'})`,
    type: 'availability_slots' as const,
    availabilitySlots: slots,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);

  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: `🗓️ Shared availability`,
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: `🗓️ Shared availability`,
  });

  return docRef.id;
}

/**
 * Write a call-trace event message into a chat room.
 * Called by the party that initiates the terminal call action (end/decline/cancel).
 */
export async function sendCallTrace(
  roomId: string,
  senderId: string,
  senderName: string,
  senderAvatar: string | undefined,
  trace: CallTraceData
): Promise<void> {
  const typeLabel = trace.callType === 'video' ? 'video' : 'audio';
  let text: string;
  switch (trace.callStatus) {
    case 'ended': {
      const mins = Math.floor((trace.durationSeconds ?? 0) / 60);
      const secs = ((trace.durationSeconds ?? 0) % 60).toString().padStart(2, '0');
      text = `📞 ${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} call · ${mins}:${secs}`;
      break;
    }
    case 'missed':
      text = `📵 Missed ${typeLabel} call`;
      break;
    case 'declined':
      text = `📵 Declined ${typeLabel} call`;
      break;
    case 'cancelled':
      text = `📵 Cancelled ${typeLabel} call`;
      break;
  }
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text,
    type: 'call',
    callTrace: trace,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);

  // Update room last-message preview
  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: text,
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: text,
  });
}

/**
 * Share app content (track, artist, release, etc.) in a chat room
 */
export async function shareContent(
  roomId: string,
  senderId: string,
  senderName: string,
  senderAvatar: string | undefined,
  content: SharedContent,
): Promise<string> {
  const previewText = `Shared: ${content.title}`;
  const msgData = {
    roomId,
    senderId,
    senderName,
    senderAvatar: senderAvatar || '',
    text: previewText,
    type: 'shared_content',
    sharedContent: content,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'chatMessages'), msgData);

  // Update room's last message
  await updateDoc(doc(db, 'chatRooms', roomId), {
    lastMessage: previewText,
    lastMessageAt: serverTimestamp(),
    lastSenderId: senderId,
    lastSenderName: senderName,
    lastMessageText: previewText,
  });

  return docRef.id;
}

/**
 * Subscribe to messages in a room (real-time)
 */
export function subscribeMessages(
  roomId: string,
  callback: (messages: ChatMessage[]) => void
): () => void {
  const q = query(
    collection(db, 'chatMessages'),
    where('roomId', '==', roomId),
    orderBy('createdAt', 'asc')
  );

  // Track the fallback unsub so we can clean it up alongside the main one.
  let fallbackUnsub: (() => void) | null = null;

  const mainUnsub = onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data(),
    } as ChatMessage));
    callback(messages);
  }, (error) => {
    console.error('Error subscribing to messages:', error);
    // If the error is about a missing index, try without orderBy as fallback
    const fallbackQ = query(
      collection(db, 'chatMessages'),
      where('roomId', '==', roomId)
    );
    fallbackUnsub = onSnapshot(fallbackQ, (snapshot) => {
      const messages = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as ChatMessage))
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return aTime - bTime;
        });
      callback(messages);
    });
  });

  // Return a combined unsub that tears down both the main and any fallback listener.
  return () => {
    mainUnsub();
    fallbackUnsub?.();
  };
}

/**
 * Delete (unsend) a message
 */
/**
 * Edit the text of a message the current user sent.
 * Only the original sender can edit their own messages.
 */
export async function editMessage(
  messageId: string,
  userId: string,
  newText: string,
): Promise<void> {
  const msgRef = doc(db, 'chatMessages', messageId);
  const msgDoc = await getDoc(msgRef);
  if (!msgDoc.exists()) throw new Error('Message not found');
  if (msgDoc.data().senderId !== userId) throw new Error('Cannot edit another user\'s message');
  await updateDoc(msgRef, {
    text: newText.trim(),
    edited: true,
    editedAt: serverTimestamp(),
  });
}

export async function deleteMessage(
  messageId: string,
  roomId: string,
  userId: string
): Promise<void> {
  // Verify the message belongs to this user
  const msgDoc = await getDoc(doc(db, 'chatMessages', messageId));
  if (!msgDoc.exists()) throw new Error('Message not found');
  const msgData = msgDoc.data();
  if (msgData.senderId !== userId) throw new Error('Cannot delete another user\'s message');

  await deleteDoc(doc(db, 'chatMessages', messageId));

  // Update room's last message if needed
  const roomMessages = query(
    collection(db, 'chatMessages'),
    where('roomId', '==', roomId),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  try {
    const snap = await getDocs(roomMessages);
    if (snap.empty) {
      await updateDoc(doc(db, 'chatRooms', roomId), {
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        lastSenderId: '',
      });
    } else {
      const lastMsg = snap.docs[0].data();
      await updateDoc(doc(db, 'chatRooms', roomId), {
        lastMessage: lastMsg.text || '',
        lastMessageAt: lastMsg.createdAt || serverTimestamp(),
        lastSenderId: lastMsg.senderId || '',
      });
    }
  } catch (err) {
    console.error('Error updating room after message delete:', err);
  }
}

/** The newest message in a room (coπ: "read the message"). */
export async function getLatestMessage(roomId: string): Promise<ChatMessage | null> {
  const snap = await getDocs(query(collection(db, 'chatMessages'), where('roomId', '==', roomId), orderBy('createdAt', 'desc'), limit(1)));
  const d = snap.docs[0];
  return d ? ({ id: d.id, ...d.data() } as ChatMessage) : null;
}

// ============ UNREAD TRACKING ============

/**
 * Mark a room as read for a user (set lastReadAt timestamp)
 */
export async function markRoomRead(roomId: string, userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'chatRooms', roomId), {
      [`lastReadAt.${userId}`]: serverTimestamp(),
    });
  } catch (err) {
    console.error('Error marking room read:', err);
  }
}

/**
 * Subscribe to total unread message count across all rooms for a user.
 * Returns unsubscribe function.
 */
export function subscribeUnreadCount(
  userId: string,
  callback: (count: number) => void
): () => void {
  const q = query(
    collection(db, 'chatRooms'),
    where('participants', 'array-contains', userId)
  );
  return onSnapshot(q, (snapshot) => {
    let total = 0;
    for (const docSnap of snapshot.docs) {
      const room = docSnap.data();
      // If last message was sent by someone else and after our lastReadAt → unread
      if (room.lastSenderId && room.lastSenderId !== userId && room.lastMessageAt) {
        const lastRead = room.lastReadAt?.[userId];
        if (!lastRead) {
          // Never read this room, and there's a message from someone else
          total++;
        } else {
          const lastReadMs = lastRead?.toMillis?.() || 0;
          const lastMsgMs = room.lastMessageAt?.toMillis?.() || 0;
          if (lastMsgMs > lastReadMs) {
            total++;
          }
        }
      }
    }
    callback(total);
  });
}

// ============ ONLINE PRESENCE ============

/**
 * Update user presence status
 */
export async function setUserOnline(userId: string, online: boolean): Promise<void> {
  try {
    await setDoc(doc(db, 'presence', userId), {
      online,
      lastSeen: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.error('Error setting presence:', err);
  }
}

/**
 * Subscribe to a set of users' online status
 */
export function subscribePresence(
  userIds: string[],
  callback: (presenceMap: Record<string, boolean>) => void
): () => void {
  if (userIds.length === 0) {
    callback({});
    return () => {};
  }

  // Firestore 'in' queries support max 30 items
  const chunks = [];
  for (let i = 0; i < userIds.length; i += 30) {
    chunks.push(userIds.slice(i, i + 30));
  }

  const unsubscribes: (() => void)[] = [];
  const presenceMap: Record<string, boolean> = {};

  for (const chunk of chunks) {
    const q = query(
      collection(db, 'presence'),
      where('__name__', 'in', chunk)
    );
    const unsub = onSnapshot(q, (snapshot) => {
      snapshot.docs.forEach(d => {
        presenceMap[d.id] = d.data().online === true;
      });
      callback({ ...presenceMap });
    });
    unsubscribes.push(unsub);
  }

  return () => unsubscribes.forEach(u => u());
}
